/**
 * DuctulatorCalculator.js
 *
 * Reactive calculator class for the Ductulator tool.
 *
 * Architecture follows HydronicsCalculator.js:
 *   - Extends InputComponent (provides Updater)
 *   - Uses Field system for all inputs/outputs
 *   - Uses Watcher.globalInstance() for reactive computations
 *   - Uses LocalStorageSaver for persistence
 *   - All field.value properties are ALWAYS in imperial units
 *   - Display units are handled by the Field system via getProjectUnits()
 */

import { setupClass, makeEnum } from '../Base.js'
import { Field, FieldType, ProjectUnits } from '../Common/Field.js'
import { InputComponent } from '../Common/InputComponent.js'
import { Watcher } from '../Common/Watcher.js'
import { LocalStorageSaver } from '../Common/LocalStorageSaver.js'
import {
  calcAirDensity,
  sizeDuctFromVelocity,
  sizeDuctFromFrictionRate,
  calcFrictionRate,
  calcRoundDuctVelocity,
  calcRectVelocity,
  calcRectFrictionRate,
  ashraEqualFrictionDiameter,
  rectDimFromEquivDiameter,
  roundRoundDiameter,
  roundUpRoundDiameter,
  roundRectDimension,
  roundUpRectDimension,
  aspectRatio,
} from '../Data/DuctHydraulics.js'

// ---------------------------------------------------------------------------
// Enum options
// ---------------------------------------------------------------------------

const CalculatorModeOptions = makeEnum({
  'Size Duct': 'Size Duct',
  'Analyze Duct': 'Analyze Duct',
})

const SizingMethodOptions = makeEnum({
  'Velocity': 'Velocity',
  'Friction Rate': 'Friction Rate',
})

const DuctShapeOptions = makeEnum({
  'Round': 'Round',
  'Rectangular': 'Rectangular',
})

const RoundingOptions = makeEnum({
  'None': 'None',
  'Round to nearest': 'Round to nearest',
  'Round up': 'Round up',
})

// ---------------------------------------------------------------------------
// DuctulatorCalculator
// ---------------------------------------------------------------------------

export class DuctulatorCalculator extends InputComponent {
  init() {
    // Global watcher — calculator lives outside any project context
    this.watcher = Watcher.globalInstance()
    this.updater.setWatcher(this.watcher)

    // ---- Units ----
    this.units = Field.makeSelect('Units', ProjectUnits, { watcher: this.watcher })
    this.units.value = 'Imperial'

    // Project context proxy so Fields can determine display units
    this.projectContext = { getProjectUnits: () => this.units.value }

    // ---- Mode ----
    this.calculatorMode = Field.makeSelect('Mode', CalculatorModeOptions, { watcher: this.watcher })
    this.calculatorMode.value = 'Size Duct'

    // ---- Sizing Method (Size Duct mode) ----
    this.sizingMethod = Field.makeSelect('Sizing input', SizingMethodOptions, { watcher: this.watcher })
    this.sizingMethod.value = 'Velocity'
    this.sizingMethod.setVisibility(() => this.calculatorMode.value === 'Size Duct')

    // ---- Rounding ----
    this.rounding = Field.makeSelect('Rounding', RoundingOptions, { watcher: this.watcher })
    this.rounding.value = 'None'

    // ---- Duct Shape (Analyze Duct mode) ----
    this.ductShape = Field.makeSelect('Duct shape', DuctShapeOptions, { watcher: this.watcher })
    this.ductShape.value = 'Round'
    this.ductShape.setVisibility(() => this.calculatorMode.value === 'Analyze Duct')

    // ========== Size Duct Inputs ==========

    this.flowRate = new Field({
      name: 'Flow Rate',
      type: FieldType.AirFlow,
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 1000,
      min: 0,
      allowMin: false,
    })
    this.flowRate.setVisibility(() => this.calculatorMode.value === 'Size Duct')

    this.designVelocity = new Field({
      name: 'Design Velocity',
      type: FieldType.AirVelocity,
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 1000, // fpm
    })
    this.designVelocity.setVisibility(() =>
      this.calculatorMode.value === 'Size Duct' &&
      this.sizingMethod.value === 'Velocity'
    )

    this.designFrictionRate = new Field({
      name: 'Friction Rate',
      type: FieldType.DuctFrictionRate,
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 0.08, // in.wc/100ft (common design value)
    })
    this.designFrictionRate.setVisibility(() =>
      this.calculatorMode.value === 'Size Duct' &&
      this.sizingMethod.value === 'Friction Rate'
    )

    // ========== Analyze Duct Inputs ==========

    this.analyzeDiameter = new Field({
      name: 'Round Duct Diameter',
      type: FieldType.SmallLength, // inches
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 12, // inches
      min: 0,
      allowMin: false,
    })
    this.analyzeDiameter.setVisibility(() =>
      this.calculatorMode.value === 'Analyze Duct' &&
      this.ductShape.value === 'Round'
    )

    this.analyzeRectWidth = new Field({
      name: 'Duct Width',
      type: FieldType.SmallLength, // inches
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 12,
      min: 0,
      allowMin: false,
    })
    this.analyzeRectWidth.setVisibility(() =>
      this.calculatorMode.value === 'Analyze Duct' &&
      this.ductShape.value === 'Rectangular'
    )

    this.analyzeRectHeight = new Field({
      name: 'Duct Height',
      type: FieldType.SmallLength, // inches
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 8,
      min: 0,
      allowMin: false,
    })
    this.analyzeRectHeight.setVisibility(() =>
      this.calculatorMode.value === 'Analyze Duct' &&
      this.ductShape.value === 'Rectangular'
    )

    this.analyzeFlowRate = new Field({
      name: 'Flow Rate',
      type: FieldType.AirFlow,
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 1000,
      min: 0,
      allowMin: false,
    })
    this.analyzeFlowRate.setVisibility(() => this.calculatorMode.value === 'Analyze Duct')

    // ========== Rectangular Duct: user-editable width ==========
    // Used in Size Duct mode for the secondary rectangular output.
    this.rectWidthInput = new Field({
      name: 'Width',
      type: FieldType.SmallLength, // inches
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 0, // 0 = auto (will be calculated)
      min: 0,
    })
    this.rectWidthInput.setVisibility(() => this.calculatorMode.value === 'Size Duct')

    // ========== Advanced Section ==========

    this.altitude = new Field({
      name: 'Altitude',
      type: FieldType.Length, // ft
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 0,
      min: -10000,
      max: 50000,
      allowMin: true,
    })

    this.airDensity = new Field({
      name: 'Air Density',
      type: FieldType.Decimal,
      isOutput: true,
      defaultValue: 0.0752,
    })

    // ========== Outputs: Size Duct mode ==========

    this.roundDiameter = new Field({
      name: 'Round Duct Diameter',
      type: FieldType.SmallLength,
      isOutput: true,
      projectContext: this.projectContext,
      defaultValue: 0,
    })
    this.roundDiameter.setVisibility(() => this.calculatorMode.value === 'Size Duct')

    this.roundVelocityOut = new Field({
      name: 'Velocity',
      type: FieldType.AirVelocity,
      isOutput: true,
      projectContext: this.projectContext,
      defaultValue: 0,
    })
    this.roundVelocityOut.setVisibility(() => this.calculatorMode.value === 'Size Duct')

    this.roundFrictionOut = new Field({
      name: 'Friction Rate',
      type: FieldType.DuctFrictionRate,
      isOutput: true,
      projectContext: this.projectContext,
      defaultValue: 0,
    })
    this.roundFrictionOut.setVisibility(() => this.calculatorMode.value === 'Size Duct')

    // Rectangular secondary outputs (Size Duct mode)
    this.rectHeightOut = new Field({
      name: 'Height',
      type: FieldType.SmallLength,
      isOutput: true,
      projectContext: this.projectContext,
      defaultValue: 0,
    })
    this.rectHeightOut.setVisibility(() => this.calculatorMode.value === 'Size Duct')

    this.rectVelocityOut = new Field({
      name: 'Velocity',
      type: FieldType.AirVelocity,
      isOutput: true,
      projectContext: this.projectContext,
      defaultValue: 0,
    })
    this.rectVelocityOut.setVisibility(() => this.calculatorMode.value === 'Size Duct')

    this.rectFrictionOut = new Field({
      name: 'Friction Rate',
      type: FieldType.DuctFrictionRate,
      isOutput: true,
      projectContext: this.projectContext,
      defaultValue: 0,
    })
    this.rectFrictionOut.setVisibility(() => this.calculatorMode.value === 'Size Duct')

    this.rectAspectRatioOut = new Field({
      name: 'Aspect Ratio',
      type: FieldType.Decimal,
      isOutput: true,
      defaultValue: 0,
    })
    this.rectAspectRatioOut.precision = 2
    this.rectAspectRatioOut.setVisibility(() => this.calculatorMode.value === 'Size Duct')

    // ========== Outputs: Analyze Duct mode ==========

    this.analyzeVelocityOut = new Field({
      name: 'Velocity',
      type: FieldType.AirVelocity,
      isOutput: true,
      projectContext: this.projectContext,
      defaultValue: 0,
    })
    this.analyzeVelocityOut.setVisibility(() => this.calculatorMode.value === 'Analyze Duct')

    this.analyzeFrictionOut = new Field({
      name: 'Friction Rate',
      type: FieldType.DuctFrictionRate,
      isOutput: true,
      projectContext: this.projectContext,
      defaultValue: 0,
    })
    this.analyzeFrictionOut.setVisibility(() => this.calculatorMode.value === 'Analyze Duct')

    // Analyze → equivalent duct output
    // If analyzing round → show equivalent rectangular (editable width)
    this.analyzeEquivRectWidth = new Field({
      name: 'Width',
      type: FieldType.SmallLength,
      watcher: this.watcher,
      projectContext: this.projectContext,
      defaultValue: 0,
      min: 0,
    })
    this.analyzeEquivRectWidth.setVisibility(() =>
      this.calculatorMode.value === 'Analyze Duct' &&
      this.ductShape.value === 'Round'
    )

    this.analyzeEquivRectHeight = new Field({
      name: 'Height',
      type: FieldType.SmallLength,
      isOutput: true,
      projectContext: this.projectContext,
      defaultValue: 0,
    })
    this.analyzeEquivRectHeight.setVisibility(() =>
      this.calculatorMode.value === 'Analyze Duct' &&
      this.ductShape.value === 'Round'
    )

    this.analyzeEquivRoundD = new Field({
      name: 'Equivalent Round Diameter',
      type: FieldType.SmallLength,
      isOutput: true,
      projectContext: this.projectContext,
      defaultValue: 0,
    })
    this.analyzeEquivRoundD.setVisibility(() =>
      this.calculatorMode.value === 'Analyze Duct' &&
      this.ductShape.value === 'Rectangular'
    )

    // Aspect ratio warning flag — not a Field, just reactive state
    this.showHighAspectWarning = false

    // ========== Wire up reactive computation ==========

    this.watcher.addWatchEffect(() => {
      this._recalculate()
    })

    // When units change, re-sync all output field display values
    this.units.makeUpdater(() => {
      this._syncAllDisplayValues()
    })

    // ========== Serialization ==========

    this.serFields = [
      'units',
      'calculatorMode',
      'sizingMethod',
      'rounding',
      'ductShape',
      'flowRate',
      'designVelocity',
      'designFrictionRate',
      'analyzeDiameter',
      'analyzeRectWidth',
      'analyzeRectHeight',
      'analyzeFlowRate',
      'rectWidthInput',
      'altitude',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Ductulator',
      path: 'ductulator',
    }

    this.saver = new LocalStorageSaver('heatwise-ductulator-calculator', this)
  }

  getProjectUnits() {
    return this.units.value === 'Metric' ? ProjectUnits.Metric : ProjectUnits.Imperial
  }

  // -------------------------------------------------------------------------
  // Core calculation
  // -------------------------------------------------------------------------

  _recalculate() {
    try {
      const rho = calcAirDensity(this.altitude.value) // lb/ft³
      // Update displayed air density (imperial only — informational)
      this.airDensity.value = rho

      if (this.calculatorMode.value === 'Size Duct') {
        this._calcSizeDuct(rho)
      } else {
        this._calcAnalyzeDuct(rho)
      }
    } catch (err) {
      console.error('[DuctulatorCalculator] Error in _recalculate:', err)
    }
  }

  _applyRoundingToRoundDiam(D_in) {
    const r = this.rounding.value
    if (r === 'Round to nearest') return roundRoundDiameter(D_in)
    if (r === 'Round up') return roundUpRoundDiameter(D_in)
    return D_in
  }

  _applyRoundingToRectDim(d_in) {
    const r = this.rounding.value
    if (r === 'Round to nearest') return roundRectDimension(d_in)
    if (r === 'Round up') return roundUpRectDimension(d_in)
    return d_in
  }

  _calcSizeDuct(rho) {
    const Q = this.flowRate.value // CFM (imperial)
    if (Q <= 0) {
      this._clearSizeDuctOutputs()
      return
    }

    // 1. Compute raw round diameter
    let D_in
    if (this.sizingMethod.value === 'Velocity') {
      const V = this.designVelocity.value // fpm (imperial)
      if (V <= 0) { this._clearSizeDuctOutputs(); return }
      D_in = sizeDuctFromVelocity(Q, V)
    } else {
      const FR = this.designFrictionRate.value // in.wc/100ft (imperial)
      if (FR <= 0) { this._clearSizeDuctOutputs(); return }
      D_in = sizeDuctFromFrictionRate(Q, FR, rho)
    }

    // 2. Save raw diameter before rounding.
    // Rectangular dimensions are rounded independently from the round duct —
    // applying round-duct rounding first would inflate the target equivalent
    // diameter, causing the height to also jump an extra standard size.
    const rawD_in = D_in

    // Apply rounding to round diameter (affects round duct outputs only)
    D_in = this._applyRoundingToRoundDiam(D_in)

    // 3. Derived values for round duct
    const V_fpm = calcRoundDuctVelocity(Q, D_in)
    const FR_out = calcFrictionRate(Q, D_in, rho)

    this.roundDiameter.value = D_in
    this.roundVelocityOut.value = V_fpm
    this.roundFrictionOut.value = FR_out

    // 4. Rectangular equivalent
    // Use rawD_in (unrounded) as the target equivalent diameter so that W and H
    // are each rounded independently. Using the already-rounded D_in here would
    // make H_in artificially large before rounding, causing a double size jump.
    let W_in = this.rectWidthInput.value
    if (W_in <= 0) {
      // Default: aim for ~1:1 to 3:1 ratio; start with width ≈ 0.6 × raw D
      W_in = Math.max(4, rawD_in * 0.6)
      W_in = this._applyRoundingToRectDim(W_in)
    } else {
      W_in = this._applyRoundingToRectDim(W_in)
    }

    let H_in = rectDimFromEquivDiameter(rawD_in, W_in)
    H_in = this._applyRoundingToRectDim(H_in)

    const recalcFR = calcRectFrictionRate(Q, W_in, H_in, rho)
    const recalcV = calcRectVelocity(Q, W_in, H_in)
    const AR = aspectRatio(W_in, H_in)

    this.rectHeightOut.value = H_in
    this.rectVelocityOut.value = recalcV
    this.rectFrictionOut.value = recalcFR
    this.rectAspectRatioOut.value = AR
    this.showHighAspectWarning = AR > 3
  }

  _calcAnalyzeDuct(rho) {
    const Q = this.analyzeFlowRate.value // CFM
    if (Q <= 0) {
      this._clearAnalyzeDuctOutputs()
      return
    }

    if (this.ductShape.value === 'Round') {
      const D = this.analyzeDiameter.value // inches
      if (D <= 0) { this._clearAnalyzeDuctOutputs(); return }

      const V = calcRoundDuctVelocity(Q, D)
      const FR = calcFrictionRate(Q, D, rho)

      this.analyzeVelocityOut.value = V
      this.analyzeFrictionOut.value = FR
      this.analyzeEquivRoundD.value = 0

      // Equivalent rectangular — use editable width input
      let W = this.analyzeEquivRectWidth.value
      if (W <= 0) {
        W = Math.max(4, D * 0.6)
      }
      const H = rectDimFromEquivDiameter(D, W)
      this.analyzeEquivRectHeight.value = H
      this.showHighAspectWarning = aspectRatio(W, H) > 3

    } else {
      // Rectangular input
      const W = this.analyzeRectWidth.value
      const H = this.analyzeRectHeight.value
      if (W <= 0 || H <= 0) { this._clearAnalyzeDuctOutputs(); return }

      const V = calcRectVelocity(Q, W, H)
      const FR = calcRectFrictionRate(Q, W, H, rho)
      const equivD = ashraEqualFrictionDiameter(W, H)

      this.analyzeVelocityOut.value = V
      this.analyzeFrictionOut.value = FR
      this.analyzeEquivRoundD.value = equivD
      this.showHighAspectWarning = aspectRatio(W, H) > 3
    }
  }

  _clearSizeDuctOutputs() {
    this.roundDiameter.value = 0
    this.roundVelocityOut.value = 0
    this.roundFrictionOut.value = 0
    this.rectHeightOut.value = 0
    this.rectVelocityOut.value = 0
    this.rectFrictionOut.value = 0
    this.rectAspectRatioOut.value = 0
    this.showHighAspectWarning = false
  }

  _clearAnalyzeDuctOutputs() {
    this.analyzeVelocityOut.value = 0
    this.analyzeFrictionOut.value = 0
    this.analyzeEquivRoundD.value = 0
    this.analyzeEquivRectHeight.value = 0
    this.showHighAspectWarning = false
  }

  // Re-sync display values after unit changes
  _syncAllDisplayValues() {
    const outputFields = [
      this.roundDiameter, this.roundVelocityOut, this.roundFrictionOut,
      this.rectHeightOut, this.rectVelocityOut, this.rectFrictionOut,
      this.analyzeVelocityOut, this.analyzeFrictionOut,
      this.analyzeEquivRoundD, this.analyzeEquivRectHeight,
      this.flowRate, this.designVelocity, this.designFrictionRate,
      this.altitude, this.analyzeDiameter, this.analyzeRectWidth,
      this.analyzeRectHeight, this.analyzeFlowRate, this.rectWidthInput,
      this.analyzeEquivRectWidth,
    ]
    for (const f of outputFields) {
      f._syncDisplayValue()
    }
  }

  loadFromStorage() {
    this.saver.loadFromStorage()
  }

  async save() {
    await this.saver.save()
  }
}

setupClass(DuctulatorCalculator)
